import React, { useEffect, useState, useMemo } from 'react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Icon } from '../lib/icons';
import { dataService } from '../services/dataService';
import { useLanguage } from '../context/LanguageContext';

export function ArteMusei() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('all');
  const { lang } = useLanguage();
  useEffect(() => { dataService.getArtMuseums().then(setData); }, [lang]);

  const filterPills = useMemo(
    () => (data?.filters || []).filter((f) => f.id !== 'all'),
    [data]
  );

  if (!data) return null;

  return (
    <div className="pb-20 pt-8">
      {/* HERO */}
      <Container>
        <section className="grid grid-cols-1 items-stretch overflow-hidden rounded-card border border-paelbi-line bg-paelbi-bg2/50 md:grid-cols-2">
          <div className="flex flex-col justify-center p-8 md:p-12">
            <h1 className="font-display text-4xl text-paelbi-ink md:text-[52px]">{data.hero.title}</h1>
            <span className="mt-4 block h-px w-12 bg-paelbi-brown/60" />
            <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-paelbi-ink/75">{data.hero.text}</p>
          </div>
          <div className="relative min-h-[240px] md:min-h-0">
            <img src={data.hero.image} alt="Arte e musei" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </section>
      </Container>

      {/* FILTERS */}
      <Container className="mt-8">
        <div className="flex flex-wrap items-center gap-3">
          <button className="flex h-11 items-center gap-2 rounded-btn border border-paelbi-line bg-white px-4 text-sm text-paelbi-ink/80">
            <Icon name="grid" size={18} /> Tutte le categorie <Icon name="chevronRight" size={14} className="rotate-90" />
          </button>
          {filterPills.map((f) => (
            <button key={f.id} onClick={() => setActive(f.id)}
              className={`h-11 rounded-btn border px-5 text-sm transition-colors ${active === f.id ? 'border-paelbi-brown bg-paelbi-brown text-white' : 'border-paelbi-line bg-white text-paelbi-ink/80 hover:border-paelbi-brown/50'}`}>
              {f.label}
            </button>
          ))}
          <button className="ml-auto flex h-11 items-center gap-2 rounded-btn border border-paelbi-line bg-white px-5 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50">
            Filtra <Icon name="sliders" size={16} />
          </button>
        </div>
      </Container>

      {/* FEATURED MUSEUMS */}
      <Container className="mt-10">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">Musei in evidenza</h2>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {data.featured.map((m) => (
            <article key={m.id} className="group flex flex-col overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card transition-all hover:-translate-y-1 hover:shadow-soft">
              <img src={m.image} alt={m.title} loading="lazy" className="h-44 w-full object-cover" />
              <div className="flex flex-1 flex-col p-6">
                <Badge className="w-fit">{m.tag}</Badge>
                <h3 className="mt-3 font-display text-lg leading-snug text-paelbi-ink">{m.title}</h3>
                <p className="mt-3 flex items-center gap-1.5 font-sans text-sm text-paelbi-ink/70">
                  <Icon name="mapPin" size={15} className="text-paelbi-brown" />{m.address}
                </p>
                <a href="#" className="mt-4 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-paelbi-brown hover:gap-2.5 transition-all">
                  Scopri di più <Icon name="arrowRight" size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </Container>

      {/* OTHER PLACES */}
      <Container className="mt-14">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">Altri luoghi d’arte</h2>
        <div className="mt-6 overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card">
          {data.others.map((o, i) => (
            <a href="#" key={o.id} className={`flex items-center gap-4 p-4 transition-colors hover:bg-paelbi-bg ${i > 0 ? 'border-t border-paelbi-line' : ''}`}>
              <img src={o.image} alt={o.title} loading="lazy" className="h-16 w-16 shrink-0 rounded-[12px] object-cover" />
              <div className="flex-1">
                <h3 className="font-display text-base text-paelbi-ink">{o.title}</h3>
                <p className="mt-0.5 flex items-center gap-1.5 font-sans text-xs text-paelbi-ink/60"><Icon name="mapPin" size={13} />{o.sub}</p>
              </div>
              <span className="font-sans text-sm text-paelbi-ink/70">{o.distance}</span>
              <Icon name="chevronRight" size={18} className="text-paelbi-ink/50" />
            </a>
          ))}
        </div>
      </Container>

      {/* CTA */}
      <Container className="mt-14">
        <div className="flex flex-col items-start gap-4 rounded-card bg-paelbi-bg2/60 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white"><Icon name="book" size={22} className="text-paelbi-brown" /></span>
            <div>
              <h3 className="font-display text-xl text-paelbi-ink">{data.cta.title}</h3>
              <p className="mt-1 font-sans text-sm text-paelbi-ink/70">{data.cta.text}</p>
            </div>
          </div>
          <Button variant="secondary" icon="arrowRight" className="shrink-0">{data.cta.button}</Button>
        </div>
      </Container>
    </div>
  );
}

export default ArteMusei;
